import { useState, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router";
import { motion, AnimatePresence } from "framer-motion";

// ── Types ──

type DialNavItem = {
  id: string;
  label: string;
  description: string;
  href: string;
  angle: number;
  media?: {
    poster?: string;
    mp4?: string;
    webm?: string;
  };
};

// ── Constants ──

const portalItems: DialNavItem[] = [
  {
    id: "inspections",
    label: "Inspections",
    description: "Operational dashboards and inspection workflows across supported sites.",
    href: "/ops",
    angle: 0,
    media: { mp4: "/media/inspections.mp4" },
  },
  {
    id: "graph",
    label: "Graph Explorer",
    description: "Explore relationships between sites, areas, campaigns, inspections, assets, and defects.",
    href: "/graph",
    angle: 72,
    media: { mp4: "/media/graph.mp4" },
  },
  {
    id: "fledge",
    label: "FLedge",
    description: "Realtime IoT monitoring and control.",
    href: "/fledge",
    angle: 144,
  },
  {
    id: "pmo",
    label: "PMO",
    description: "Access your Preventive Maintenance Strategies & Tactics.",
    href: "/pmo",
    angle: 216,
  },
  {
    id: "admin",
    label: "Admin",
    description: "Manage advanced tooling and administrative access where permitted.",
    href: "/admin",
    angle: 288,
  },
];

// Routes that actually exist — others render as coming-soon
const ACTIVE_ROUTES = new Set(["/ops", "/graph"]);

// ── Utilities ──

function angularDistance(a: number, b: number): number {
  const diff = Math.abs(a - b) % 360;
  return diff > 180 ? 360 - diff : diff;
}

function polarToCartesian(cx: number, cy: number, radius: number, angle: number) {
  const radians = (angle - 90) * (Math.PI / 180);
  return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) };
}

function pointerAngle(cx: number, cy: number, px: number, py: number): number {
  const dx = px - cx;
  const dy = py - cy;
  let angle = Math.atan2(dx, -dy) * (180 / Math.PI);
  if (angle < 0) angle += 360;
  return angle;
}

// ── SVG Sub-components ──

const CX = 87.61;
const CY = 87.61;

// Logo geometry: 2 thick rings with a gap between them
const OUTER_RING_R = 70.525; // (76.23 + 64.82) / 2
const INNER_RING_R = 48.195; // (53.9 + 42.49) / 2
const RING_THICKNESS = 11.41; // 76.23 - 64.82
const ARC_RADIUS = 59.36; // midpoint of gap between rings
const ARC_THICKNESS = 10.92; // gap width (64.82 - 53.9)

function ProgressArc({ targetAngle }: { targetAngle: number }) {
  const circumference = 2 * Math.PI * ARC_RADIUS;
  const strokeFraction = targetAngle / 360;
  const dashOffset = circumference * (1 - strokeFraction);

  return (
    <motion.circle
      cx={CX}
      cy={CY}
      r={ARC_RADIUS}
      fill="none"
      stroke="#e9e1b5"
      strokeWidth={ARC_THICKNESS}
      strokeLinecap="butt"
      strokeDasharray={circumference}
      initial={{ strokeDashoffset: circumference }}
      animate={{ strokeDashoffset: dashOffset }}
      transition={{ type: "spring", stiffness: 80, damping: 20 }}
      transform={`rotate(-90 ${CX} ${CY})`}
      opacity={0.85}
    />
  );
}

function CalibrationMarkSVG() {
  return (
    <>
      {/* Outer thick ring */}
      <circle
        cx={CX}
        cy={CY}
        r={OUTER_RING_R}
        fill="none"
        stroke="#1f3a32"
        strokeWidth={RING_THICKNESS}
      />
      {/* Inner thick ring */}
      <circle
        cx={CX}
        cy={CY}
        r={INNER_RING_R}
        fill="none"
        stroke="#1f3a32"
        strokeWidth={RING_THICKNESS}
      />
    </>
  );
}

function TickMarks({ items, activeId }: { items: DialNavItem[]; activeId: string }) {
  return (
    <>
      {items.map((item) => {
        // Ticks span the gap between inner ring outer edge and outer ring inner edge
        const inner = polarToCartesian(CX, CY, INNER_RING_R + RING_THICKNESS / 2 + 1, item.angle);
        const outer = polarToCartesian(CX, CY, OUTER_RING_R - RING_THICKNESS / 2 - 1, item.angle);
        const isActive = item.id === activeId;
        return (
          <motion.line
            key={item.id}
            x1={inner.x}
            y1={inner.y}
            x2={outer.x}
            y2={outer.y}
            stroke="#e9e1b5"
            strokeWidth={isActive ? 2.5 : 1.5}
            strokeLinecap="round"
            animate={{ opacity: isActive ? 1 : 0.35 }}
            transition={{ duration: 0.2 }}
          />
        );
      })}
    </>
  );
}

function CentreMedia({ activeItem }: { activeItem: DialNavItem }) {
  const gradients: Record<string, string> = {
    inspections: "from-fl-forest/60 to-fl-evergreen/80",
    graph: "from-fl-evergreen/50 to-fl-leaf/30",
    fledge: "from-fl-sand/20 to-fl-evergreen/70",
    pmo: "from-fl-forest/40 to-fl-sand/15",
    admin: "from-fl-evergreen/80 to-fl-forest/60",
  };

  const hasVideo = !!activeItem.media?.mp4;

  // Inner edge of inner green ring: radius 42.49 → diameter ≈ 48.5% of viewBox
  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{ pointerEvents: "none" }}
    >
      <AnimatePresence mode="wait">
        {hasVideo ? (
          <motion.video
            key={activeItem.id}
            className="h-[48.5%] w-[48.5%] rounded-full object-cover"
            src={activeItem.media!.mp4}
            poster={activeItem.media!.poster}
            autoPlay
            loop
            muted
            playsInline
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.3 }}
          />
        ) : (
          <motion.div
            key={activeItem.id}
            className={`h-[48.5%] w-[48.5%] rounded-full bg-gradient-to-br ${gradients[activeItem.id] ?? "from-fl-forest/60 to-fl-evergreen/80"}`}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.92 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Main Component ──

export default function CalibrationDialNav() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string>(portalItems[0].id);

  const activeItem = useMemo(
    () => portalItems.find((i) => i.id === activeId) ?? portalItems[0],
    [activeId],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const angle = pointerAngle(cx, cy, e.clientX, e.clientY);

    let closest = portalItems[0];
    let minDist = 360;
    for (const item of portalItems) {
      const dist = angularDistance(angle, item.angle);
      if (dist < minDist) {
        minDist = dist;
        closest = item;
      }
    }
    setActiveId(closest.id);
  }, []);

  const handleFocus = useCallback((id: string) => {
    setActiveId(id);
  }, []);

  return (
    <div className="flex flex-col items-center gap-6 w-full max-w-[420px] lg:max-w-[480px]">
      {/* Dial */}
      <div
        ref={containerRef}
        className="relative aspect-square w-full select-none"
        onPointerMove={handlePointerMove}
      >
        {/* SVG Layer */}
        <svg
          viewBox="0 0 175.27 175.27"
          className="absolute inset-0 h-full w-full"
          aria-hidden="true"
        >
          <CalibrationMarkSVG />
          <ProgressArc targetAngle={activeItem.angle} />
          <TickMarks items={portalItems} activeId={activeId} />
        </svg>

        {/* Centre media */}
        <CentreMedia activeItem={activeItem} />

        {/* Labels around the dial */}
        <nav aria-label="Portal navigation" className="absolute inset-0">
          {portalItems.map((item) => {
            const isActive = item.id === activeId;
            const isRouteActive = ACTIVE_ROUTES.has(item.href);
            // Position label outside the outer ring
            const labelRadius = 54; // percent from centre
            const pos = polarToCartesian(50, 50, labelRadius, item.angle);

            // Align labels based on which side of the dial they sit on
            // Right side → left-align (text extends right from anchor)
            // Left side  → right-align (text extends left from anchor)
            // Top/bottom → centre
            const isRight = item.angle > 15 && item.angle < 165;
            const isLeft = item.angle > 195 && item.angle < 345;
            const translateX = isRight
              ? "translate-x-0"
              : isLeft
                ? "-translate-x-full"
                : "-translate-x-1/2";
            const textAlign = isRight
              ? "text-left"
              : isLeft
                ? "text-right"
                : "text-center";

            const labelContent = (
              <motion.span
                className={`block max-w-[8rem] text-xs font-semibold uppercase tracking-[0.2em] transition-colors lg:text-sm ${textAlign} ${
                  isActive ? "text-fl-evergreen" : "text-fl-evergreen/50"
                } ${!isRouteActive ? "italic" : ""}`}
                animate={{
                  scale: isActive ? 1.08 : 1,
                  opacity: isActive ? 1 : 0.6,
                }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
              >
                {item.label}
                {!isRouteActive && (
                  <span className="ml-1 text-[0.6rem] not-italic text-fl-evergreen/35">soon</span>
                )}
              </motion.span>
            );

            const commonProps = {
              className: `absolute ${translateX} -translate-y-1/2 outline-none focus-visible:ring-2 focus-visible:ring-fl-evergreen/60 rounded px-1`,
              style: { left: `${pos.x}%`, top: `${pos.y}%` },
              onFocus: () => handleFocus(item.id),
              "aria-label": `${item.label}${!isRouteActive ? " (coming soon)" : ""}`,
            };

            // Active routes get a real Link; others are buttons with no navigation
            if (isRouteActive) {
              return (
                <Link key={item.id} to={item.href} {...commonProps}>
                  {labelContent}
                </Link>
              );
            }

            // Coming-soon items — rendered as span/button, not navigable
            return (
              <button
                key={item.id}
                type="button"
                {...commonProps}
                // No navigation for routes that don't exist yet
                aria-disabled="true"
                onClick={(e) => e.preventDefault()}
              >
                {labelContent}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Description panel — fixed height to prevent layout shift */}
      <div className="h-[4.5rem] max-w-sm text-center flex items-start justify-center">
        <AnimatePresence mode="wait">
          <motion.p
            key={activeItem.id}
            className="text-sm leading-relaxed text-fl-evergreen/70"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22 }}
          >
            {activeItem.description}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
}
